import { Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";

interface VersionBadgeProps {
  versionNumber: number | null | undefined;
  /**
   * User-supplied semver string (e.g. "1.0.3"). When present, replaces
   * the `v{N}` fallback so users see the label they chose. The internal
   * version_number is still passed through `title` so it's discoverable
   * on hover.
   */
  semverLabel?: string | null | undefined;
  isCurrent?: boolean;
  imported?: boolean;
  onClick?: () => void;
  title?: string;
  size?: "small" | "medium";
}

/**
 * Compact version chip. Renders the user's semver label when set
 * (e.g. "1.0.3 · current"), otherwise falls back to `v{N} · current`
 * so legacy versions stay readable. Mirrors the tonal styling used by
 * ExtractionBadge.
 */
export function VersionBadge({
  versionNumber,
  semverLabel,
  isCurrent,
  imported,
  onClick,
  title,
  size = "small",
}: VersionBadgeProps) {
  if (versionNumber == null) return null;

  const palette = isCurrent
    ? ("success" as const)
    : imported
      ? ("warning" as const)
      : ("primary" as const);

  const base = semverLabel?.trim() || `v${versionNumber}`;
  const label = imported
    ? `${base} · imported`
    : isCurrent
      ? `${base} · current`
      : base;

  // Always surface the internal version_number on hover — useful when
  // the user uses semver labels and wants to know the monotonic id too.
  const fullTitle =
    title ??
    (semverLabel?.trim() ? `${base} (internal v${versionNumber})` : undefined);

  return (
    <Chip
      size={size}
      label={label}
      clickable={!!onClick}
      onClick={onClick}
      title={fullTitle}
      sx={(t) => ({
        bgcolor: alpha(t.palette[palette].main, 0.12),
        color: t.palette[palette].main,
        border: `1px solid ${alpha(t.palette[palette].main, 0.35)}`,
        fontWeight: 600,
        cursor: onClick ? "pointer" : "default",
      })}
    />
  );
}
